import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  EmployeeAvatar,
  Button,
  Card,
  Drawer,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Tabs,
  useConfirm,
  useToast,
} from "../../design-system";
import {
  approveAbsence,
  cancelAbsence,
  cancelOvertimeRequest,
  decideOvertimeRequest,
  getAbsences,
  getEmployees,
  getMyOvertimeRequests,
  getOvertimeRequests,
  rejectAbsence,
} from "../../helpers/kronaly_helper";
import {
  ABSENCE_STATUS_LABEL,
  ABSENCE_STATUS_TONE,
  ABSENCE_TYPE_LABEL,
  ABSENCE_TYPE_TONE,
  formatDays,
  formatRange,
} from "../../Components/Kronaly/Ausencias/absenceHelpers";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import { useStartTour } from "../../tours/TourContext";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const fullName = (e) => {
  if (!e) return "—";
  const n = `${e.first_name || ""} ${e.last_name || ""}`.trim();
  return n || "(sin nombre)";
};

/**
 * Status normalizado para la UI: ambos kinds usan los mismos valores de
 * `uiStatus` (pending/approved/rejected/cancelled). El status real va
 * separado en `apiStatus` por si algún día divergen.
 *  - absence: pending → pending (idéntico)
 *  - overtime: open → pending (renaming en UI)
 */
function normalizeStatus(kind, status) {
  if (kind === "overtime" && status === "open") return "pending";
  return status;
}

function formatMinutes(m) {
  if (m == null) return "—";
  const n = Math.max(0, Number(m) || 0);
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const mm = n % 60;
  return mm === 0 ? `${h} h` : `${h} h ${mm} min`;
}

function Solicitudes({ mode = "inbox" }) {
  // Dos pantallas servidas por el mismo componente:
  //  - mode="inbox"  → RRHH › Solicitudes: bandeja de aprobación (todas las del
  //    scope del aprobador). Botones aprobar/rechazar.
  //  - mode="mine"   → Personal › Mis solicitudes: solo las propias del usuario
  //    (incluido un manager). Sin aprobar/rechazar; solo cancelar las propias.
  const isInbox = mode === "inbox";
  document.title = `${isInbox ? "Solicitudes" : "Mis solicitudes"} | Kronaly`;
  const toast = useToast();
  const confirm = useConfirm();
  // El tour está orientado a la bandeja de aprobación; no en "mis solicitudes".
  useStartTour(isInbox ? "solicitudes" : null);

  const { can, employeeProfileId } = useCurrentMembership();
  // Las acciones de aprobar/rechazar siguen gateadas por la capability concreta
  // de cada tipo; en modo "mine" nunca se aprueban solicitudes propias.
  const canApproveAbsences = can("absences:approve");
  const canApproveOvertime = can("overtime:approve");

  const [tab, setTab] = useState("pending");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [minutesApproved, setMinutesApproved] = useState("");
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Bandeja: el aprobador ve todas las de su scope. Mine: solo las propias.
      const overtimeFetch = isInbox
        ? getOvertimeRequests().catch(() => ({ data: [] }))
        : getMyOvertimeRequests().catch(() => ({ data: [] }));
      // En "mine" pedimos include_team (devuelve propias + aprobadas de
      // compañeros) y filtramos a las propias abajo; en inbox, la vista del
      // backend ya devuelve la bandeja del aprobador.
      const absencesFetch = isInbox
        ? getAbsences().catch(() => ({ data: [] }))
        : getAbsences({ include_team: "true" }).catch(() => ({ data: [] }));
      const [absRes, otRes, empRes] = await Promise.all([
        absencesFetch,
        overtimeFetch,
        getEmployees().catch(() => ({ data: [] })),
      ]);

      let absRaw = unwrap(absRes) || [];
      if (!isInbox && employeeProfileId) {
        absRaw = absRaw.filter((a) => a.employee_profile_id === employeeProfileId);
      }
      const absences = absRaw.map((a) => ({
        kind: "absence",
        id: a.id,
        employee_profile_id: a.employee_profile_id,
        type: a.type,
        displayType: ABSENCE_TYPE_LABEL[a.type] || a.type,
        typeTone: ABSENCE_TYPE_TONE[a.type] || "neutral",
        apiStatus: a.status,
        uiStatus: normalizeStatus("absence", a.status),
        start_date: a.start_date,
        end_date: a.end_date,
        days_count: a.days_count,
        reason: a.reason,
        decision_comment: a.decision_comment,
        created_at: a.created_at,
        raw: a,
      }));
      const overtime = (unwrap(otRes) || []).map((o) => ({
        kind: "overtime",
        id: o.id,
        employee_profile_id: o.employee_profile_id,
        type: "overtime",
        displayType: "Horas extra",
        typeTone: "warning",
        apiStatus: o.status,
        uiStatus: normalizeStatus("overtime", o.status),
        // Para visualización en columnas compartidas: usamos created_at como
        // "fecha" y los minutos como "detalle/cantidad".
        start_date: (o.created_at || "").slice(0, 10) || null,
        end_date: (o.created_at || "").slice(0, 10) || null,
        days_count: null,
        minutes_requested: o.minutes_requested ?? null,
        minutes_approved: o.minutes_approved ?? null,
        time_entry_id: o.time_entry_id ?? null,
        reason: o.note,
        decision_comment: o.decision_note ?? null,
        created_at: o.created_at,
        raw: o,
      }));

      setRows([...absences, ...overtime]);
      setEmployees(unwrap(empRes) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las solicitudes.");
    } finally {
      setLoading(false);
    }
  }, [isInbox, employeeProfileId]);

  useEffect(() => {
    load();
  }, [load]);

  const employeesById = useMemo(() => {
    const map = {};
    employees.forEach((e) => { if (e?.id) map[e.id] = e; });
    return map;
  }, [employees]);

  const counts = useMemo(() => ({
    pending: rows.filter((r) => r.uiStatus === "pending").length,
    approved: rows.filter((r) => r.uiStatus === "approved").length,
    rejected: rows.filter((r) => r.uiStatus === "rejected").length,
    cancelled: rows.filter((r) => r.uiStatus === "cancelled").length,
  }), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => r.uiStatus === tab)
      .filter((r) => {
        if (type === "all") return true;
        if (type === "overtime") return r.kind === "overtime";
        return r.kind === "absence" && r.type === type;
      })
      .filter((r) => {
        if (!q) return true;
        const emp = employeesById[r.employee_profile_id];
        const haystack = [
          fullName(emp), emp?.email, emp?.position,
          r.displayType, r.reason,
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [rows, tab, type, search, employeesById]);

  const onSelect = (row) => {
    setSelected(row);
    setComment("");
    // Para overtime, pre-rellenamos minutes_approved con minutes_requested.
    setMinutesApproved(
      row.kind === "overtime" && row.minutes_requested != null
        ? String(row.minutes_requested)
        : "",
    );
  };

  // Solo en la bandeja (inbox) se aprueba/rechaza, y siempre gateado por la
  // capability concreta del tipo. En "mine" nunca se actúa sobre lo propio.
  const canActOnSelected = (row) =>
    isInbox && (row?.kind === "absence" ? canApproveAbsences : canApproveOvertime);

  const onApprove = async () => {
    if (!selected) return;
    setActing(true);
    try {
      if (selected.kind === "absence") {
        await approveAbsence(selected.id, comment.trim() ? { comment: comment.trim() } : {});
      } else {
        const minutes = minutesApproved.trim() ? Number(minutesApproved.trim()) : null;
        if (minutes != null && (Number.isNaN(minutes) || minutes < 0)) {
          toast.error("Minutos inválidos", "Introduce un número positivo o deja en blanco.");
          setActing(false);
          return;
        }
        await decideOvertimeRequest(selected.id, {
          decision: "approved",
          minutes_approved: minutes,
          note: comment.trim() || null,
        });
      }
      toast.success("Solicitud aprobada");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("No se pudo aprobar", typeof e === "string" ? e : e?.message);
    } finally {
      setActing(false);
    }
  };

  const onReject = async () => {
    if (!selected) return;
    setActing(true);
    try {
      if (selected.kind === "absence") {
        await rejectAbsence(selected.id, comment.trim() ? { comment: comment.trim() } : {});
      } else {
        await decideOvertimeRequest(selected.id, {
          decision: "rejected",
          note: comment.trim() || null,
        });
      }
      toast.success("Solicitud rechazada");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("No se pudo rechazar", typeof e === "string" ? e : e?.message);
    } finally {
      setActing(false);
    }
  };

  const onCancel = async () => {
    if (!selected) return;
    const label = selected.kind === "overtime"
      ? "horas extra"
      : (ABSENCE_TYPE_LABEL[selected.type] || "ausencia");
    const ok = await confirm({
      title: "Cancelar solicitud",
      description: `Cancelar la ${label} de ${fullName(employeesById[selected.employee_profile_id])}.`,
      confirmLabel: "Cancelar solicitud",
      cancelLabel: "No",
      tone: "danger",
    });
    if (!ok) return;
    setActing(true);
    try {
      if (selected.kind === "absence") {
        await cancelAbsence(selected.id);
      } else {
        await cancelOvertimeRequest(selected.id);
      }
      toast.success("Solicitud cancelada");
      setSelected(null);
      load();
    } catch (e) {
      toast.error("No se pudo cancelar", typeof e === "string" ? e : e?.message);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow={isInbox ? "Bandeja de aprobaciones" : "Personal"}
        title={isInbox ? "Solicitudes" : "Mis solicitudes"}
        description={
          isInbox
            ? "Aprueba o rechaza ausencias y horas extra del equipo."
            : "Consulta y cancela tus solicitudes de ausencias y horas extra."
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <Card padded={false}>
        <div data-tour="solicitudes-tabs" style={{ padding: "12px 16px 0" }}>
          <Tabs
            tabs={[
              { id: "pending", label: "Pendientes", count: counts.pending },
              { id: "approved", label: "Aprobadas", count: counts.approved },
              { id: "rejected", label: "Rechazadas", count: counts.rejected },
              { id: "cancelled", label: "Canceladas", count: counts.cancelled },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 16px",
            borderBottom: "1px solid var(--kr-border-subtle)",
            flexWrap: "wrap",
          }}
        >
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por empleado, email, motivo…"
            icon="Search"
            style={{ flex: 1, maxWidth: 360 }}
          />
          <span style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginLeft: 8 }}>
            Tipo
          </span>
          {[
            { id: "all", label: "Todos" },
            { id: "vacation", label: "Vacaciones" },
            { id: "personal", label: "Asunto propio" },
            { id: "sick", label: "Baja médica" },
            { id: "parental", label: "Parental" },
            { id: "other", label: "Otro" },
            { id: "overtime", label: "Horas extra" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setType(t.id)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                height: 28, padding: "0 10px",
                fontSize: 12, fontWeight: 500,
                border: `1px solid ${type === t.id ? "var(--kr-action)" : "var(--kr-border)"}`,
                background: type === t.id ? "var(--kr-action-soft-bg)" : "#fff",
                color: type === t.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-strong)",
                borderRadius: 6, cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={16} /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="Inbox"
            title={`Sin solicitudes ${
              tab === "pending" ? "pendientes" :
              tab === "approved" ? "aprobadas" :
              tab === "rejected" ? "rechazadas" :
              "canceladas"
            }`}
            description={search.trim() ? "Prueba con otra búsqueda." : "Cuando lleguen solicitudes nuevas aparecerán aquí."}
          />
        ) : (
          <div data-tour="solicitudes-list">
            <SolicitudesTable
              rows={filtered}
              employeesById={employeesById}
              onOpen={onSelect}
            />
          </div>
        )}
      </Card>

      <Drawer
        open={!!selected}
        onClose={() => { setSelected(null); }}
        size="lg"
        title="Detalle de solicitud"
        footer={
          selected ? (
            <>
              {canActOnSelected(selected) && selected.uiStatus === "pending" && (
                <>
                  <Button variant="dangerSoft" icon="X" onClick={onReject} disabled={acting}>Rechazar</Button>
                  <div style={{ flex: 1 }} />
                  <Button variant="primary" icon="Check" onClick={onApprove} disabled={acting}>Aprobar</Button>
                </>
              )}
              {(selected.uiStatus === "pending" || selected.uiStatus === "approved") && (
                <Button variant="dangerSoft" icon="XCircle" onClick={onCancel} disabled={acting}>Cancelar</Button>
              )}
            </>
          ) : null
        }
      >
        {selected && (
          <SolicitudDetail
            row={selected}
            employee={employeesById[selected.employee_profile_id]}
            comment={comment}
            setComment={setComment}
            minutesApproved={minutesApproved}
            setMinutesApproved={setMinutesApproved}
            canAct={canActOnSelected(selected)}
          />
        )}
      </Drawer>
    </div>
  );
}

function SolicitudesTable({ rows, employeesById, onOpen }) {
  const cols = "1.6fr 1.2fr 1.4fr 0.9fr 1.3fr 1fr";
  return (
    <div>
      <div
        style={{
          display: "grid", gridTemplateColumns: cols, gap: 12,
          padding: "10px 20px", background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em",
          fontWeight: 600, color: "var(--kr-fg-muted)",
        }}
      >
        <span>Empleado</span>
        <span>Tipo</span>
        <span>Fechas</span>
        <span>Cantidad</span>
        <span>Motivo</span>
        <span>Estado</span>
      </div>
      {rows.map((row, i) => (
        <SolicitudRow
          key={`${row.kind}:${row.id}`}
          row={row}
          employee={employeesById[row.employee_profile_id]}
          cols={cols}
          last={i === rows.length - 1}
          onOpen={() => onOpen(row)}
        />
      ))}
    </div>
  );
}

function SolicitudRow({ row, employee, cols, last, onOpen }) {
  const [hover, setHover] = useState(false);
  const quantity = row.kind === "overtime"
    ? formatMinutes(row.minutes_approved ?? row.minutes_requested)
    : formatDays(row.days_count);
  const range = row.kind === "overtime"
    ? (row.start_date || "—")
    : formatRange(row.start_date, row.end_date);

  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      style={{
        display: "grid", gridTemplateColumns: cols, gap: 12,
        alignItems: "center", padding: "12px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff",
        cursor: "pointer", fontSize: 13,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <EmployeeAvatar employeeId={employee?.id} hasAvatar={!!employee?.has_avatar} name={fullName(employee)} size={28} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {fullName(employee)}
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{employee?.position || ""}</div>
        </div>
      </div>
      <Badge tone={row.typeTone}>{row.displayType}</Badge>
      <span style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)" }}>
        {range}
      </span>
      <span style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>
        {quantity}
      </span>
      <span style={{ fontSize: 12, color: "var(--kr-fg-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {row.reason || "—"}
      </span>
      <Badge tone={ABSENCE_STATUS_TONE[row.uiStatus]} dot>{ABSENCE_STATUS_LABEL[row.uiStatus]}</Badge>
    </div>
  );
}

function SolicitudDetail({ row, employee, comment, setComment, minutesApproved, setMinutesApproved, canAct }) {
  const isOvertime = row.kind === "overtime";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <EmployeeAvatar employeeId={employee?.id} hasAvatar={!!employee?.has_avatar} name={fullName(employee)} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
            {fullName(employee)}
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>
            {employee?.position || ""}
            {employee?.email ? ` · ${employee.email}` : ""}
          </div>
        </div>
        <Badge tone={ABSENCE_STATUS_TONE[row.uiStatus]} dot>{ABSENCE_STATUS_LABEL[row.uiStatus]}</Badge>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <DrawerStat label="Tipo">
          <Badge tone={row.typeTone}>{row.displayType}</Badge>
        </DrawerStat>
        {isOvertime ? (
          <>
            <DrawerStat label="Solicitados" mono>{formatMinutes(row.minutes_requested)}</DrawerStat>
            <DrawerStat label="Aprobados" mono>{formatMinutes(row.minutes_approved)}</DrawerStat>
            <DrawerStat label="Fecha" mono>{row.start_date || "—"}</DrawerStat>
          </>
        ) : (
          <>
            <DrawerStat label="Días" mono>{formatDays(row.days_count)}</DrawerStat>
            <DrawerStat label="Inicio" mono>{row.start_date}</DrawerStat>
            <DrawerStat label="Fin" mono>{row.end_date}</DrawerStat>
          </>
        )}
      </div>

      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 6 }}>
          {isOvertime ? "Nota del solicitante" : "Motivo"}
        </div>
        <div
          style={{
            padding: 12, background: "var(--kr-neutral-50)",
            borderRadius: 8, fontSize: 13, color: "var(--kr-fg-strong)", lineHeight: 1.5,
          }}
        >
          {row.reason || (isOvertime ? "Sin nota." : "Sin motivo indicado.")}
        </div>
      </div>

      {row.decision_comment && (
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 6 }}>
            Comentario de la decisión
          </div>
          <div style={{ padding: 12, background: "var(--kr-bg-subtle)", borderRadius: 8, fontSize: 13, color: "var(--kr-fg-strong)" }}>
            {row.decision_comment}
          </div>
        </div>
      )}

      {row.uiStatus === "pending" && canAct && (
        <>
          {isOvertime && (
            <Field label="Minutos a aprobar (opcional — vacío para mantener los solicitados)">
              <Input
                type="number"
                min={0}
                value={minutesApproved}
                onChange={(e) => setMinutesApproved(e.target.value)}
                placeholder={row.minutes_requested != null ? `${row.minutes_requested}` : "Ej. 60"}
              />
            </Field>
          )}
          <Field label={isOvertime ? "Nota para el solicitante (opcional)" : "Comentario para el empleado (opcional)"}>
            <Input
              type="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
              placeholder={isOvertime ? "Ej. Aprobado, registrado en jornada." : "Ej. Aprobada. Que disfrutes."}
            />
          </Field>
        </>
      )}
    </div>
  );
}

function DrawerStat({ label, children, mono }) {
  return (
    <div style={{ padding: 12, border: "1px solid var(--kr-border-subtle)", borderRadius: 8 }}>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginBottom: 4 }}>{label}</div>
      <div
        style={{
          color: "var(--kr-fg-strong)", fontSize: 13, fontWeight: 500,
          fontFamily: mono ? "var(--kr-font-mono)" : "var(--kr-font-sans)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default Solicitudes;
